import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  FC,
  ReactNode,
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { getPopularSearch, getSearchProduct } from '@/api/item';
import { getBoughtBefore } from '@/api/so';
import { defaultBack } from '@/components/common/HeaderNew';
import { WeeeIcon } from '@/components/common/Icon';
import { ClickTargetType, PageNameV2, SearchLandingMod, SearchTriggerType } from '@/constants/track';
import useDebounce from '@/hooks/debounce';
import useLazy from '@/hooks/useLazy';
import Tracker from '@/tracks/tracker';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getUrlQuery } from '@/utils/tool';

import Popup from '@/components/common/Popup';
import GroupOrderHeader from '@/components/group-order/components/GroupOrderHeader';
import { isSayWeeeApp } from '@/utils/common/device';
import { twMerge } from 'tailwind-merge';
import MiniCart from '../MiniCartIcon';
import { CroppedImage } from '@weee-fe/module';

const replaceHighlight = (label: string, keyword: string) => {
  return label.replace(keyword, `<span>${keyword}</span>`);
};

export type SearchOverlayProps = {
  className?: string;
  isShow?: boolean;
  isSupportTabBar?: boolean;
  onCancel?: (back?) => void;
  onFocus?: (e?) => void;
  onSearch?: (keyword?: string, trigger_type?: string) => void;
  moduleName?: string;
  children?: ReactNode;
  routerUrl?: string;
  /** 是否支持热词 */
  isSupportHotKeyword?: boolean;
  /** 是否支持联想 */
  isSupportAssoation?: boolean;
  /** 是否支持曾经购买 */
  isSupportBought?: boolean;
  /** 是否展示历史记录 */
  hasHistory?: boolean;
  /** 是否添加识别触发方式 */
  isTriggerType?: boolean;
  /** \<input />是否自动获取焦点 默认值为 true */
  inputAutoFocus?: boolean;
  placeholder?: any;
  /** 如果是在vendor 首页搜索 需要知道vendor id */
  vendorId?: number;
  vendorTitle?: string;
  focusChange?: (focus: boolean) => void;
  isInGroupOrder?: boolean;
  showMiniCart?: boolean;
  handleToCart?: (e) => void;
  shareInfo?: any;
  fromDrawer?: boolean;
  popupProps?: ObjectType;
  bodyFixed?: boolean;
  streamlinedHeader?: boolean;
  searchHandlerRef?: React.MutableRefObject<HTMLDivElement>;
  onSearchOverlayVisibilityChange?: any;
  onSearchOverlayKeywordChange?: any;
  embbedInsidePage?: boolean /* Kills suggestions/history DOM - used only when such feature does not exist. Backward compatibility to have search inside the page + global nav on core pages like orders and helpcenter */;
  onClearInputResetsEmbeddedPage?: () => void /* When the UX becomes search input embedded inside the page, clearing rhe field should trigger page to reset to initial page state content */;
} & ObjectType;

export const SearchContainer: FC<SearchOverlayProps> = forwardRef((props: SearchOverlayProps, ref) => {
  const {
    className,
    onCancel,
    moduleName = '',
    onSearch,
    routerUrl = '/search',
    onFocus,
    isSupportHotKeyword = true,
    isSupportAssoation = true,
    isSupportBought = true,
    hasHistory = true,
    isTriggerType = false,
    inputAutoFocus = true,
    placeholder,
    vendorId,
    vendorTitle = '',
    isInGroupOrder,
    showMiniCart = true,
    embbedInsidePage = false,
    handleToCart,
    shareInfo,
    fromDrawer = false,
    bodyFixed = true,
    streamlinedHeader = false,
    onSearchOverlayVisibilityChange,
    onSearchOverlayKeywordChange,
    onClearInputResetsEmbeddedPage
  } = props;

  const { t } = useTranslation('common');
  const router = useRouter();
  const query = router?.query as {
    [key: string]: any;
  };
  const searchInput = useRef(null);
  const [keyword, setKeyword] = useState<string>(query?.keyword || getUrlQuery('keyword'));
  const setKeywordAndNotify = useCallback(
    (newKeyword: string) => {
      setKeyword(newKeyword);
      onSearchOverlayKeywordChange?.(newKeyword);
    },
    [onSearchOverlayKeywordChange]
  );

  const [focus, setFocus] = useState<boolean>(false);
  const setFocusAndNotify = useCallback(
    (newFocus: boolean) => {
      setFocus(newFocus);
      onSearchOverlayVisibilityChange?.(newFocus);
    },
    [onSearchOverlayVisibilityChange]
  );

  const [pageState, setPageState] = useState<ObjectType>({});
  const [suggestList, setSuggestList] = useState([]);
  const [historyData, setHistoryData] = useState<string[]>([]);
  const { commonClick } = useClickActionT2();
  const { popular = [], bought = [] } = pageState;

  const isVendor = moduleName === 'vendor' && !!vendorId;

  const historyKey = useMemo(() => {
    return `${moduleName}searchHistory`;
  }, [moduleName]);

  const handleInputEnter = e => {
    if (e.key === 'Enter') {
      let searchValue = e?.target?.value;
      if (!searchValue && placeholder) {
        searchValue = placeholder;
      }
      isTriggerType ? handleSearch(searchValue, SearchTriggerType.active) : handleSearch(searchValue);
      commonClick({
        mod_nm: SearchLandingMod.searchBar,
        mod_pos: 0,
        co: {
          target_nm: searchValue,
          target_type: ClickTargetType.searchTarget
        },
        ctx: vendorId ? { global_vendor: vendorId } : {}
      });
    }
  };

  const handleClearKeyword = e => {
    setKeywordAndNotify('');
    window.scrollTo({
      top: 0
    });
    searchInput?.current?.focus();
    if (onClearInputResetsEmbeddedPage) {
      onClearInputResetsEmbeddedPage();
    }
  };

  const handleInputKeyword = e => {
    const val = e?.target?.value;
    setKeywordAndNotify(val);
  };

  const handleInputFocus = e => {
    setFocusAndNotify(true);
    onFocus?.(e);
    setIosFix();
  };

  const handleSearch = (value?, trigger_type?) => {
    if (onClearInputResetsEmbeddedPage && value === '') {
      onClearInputResetsEmbeddedPage();
    }

    saveSearchedData(value);
    if (onSearch) {
      setFocusAndNotify(false);
      searchInput?.current?.blur();
      return onSearch(value, trigger_type);
    }
    const urlObj = new URL(window.location.origin + routerUrl);
    const sp = urlObj.searchParams;
    if (router?.query?.page_params) {
      sp.set('page_params', router.query.page_params as string);
    }
    sp.set('fullscreen', '1');
    sp.set('StatusColor', '1');
    sp.set('is_group_order', '1');
    sp.set('group_order_key', router?.query?.key as string);

    sp.set('keyword', value);
    trigger_type && sp.set('trigger_type', trigger_type);
    router.replace(urlObj.toString());
  };

  const saveVendorSearchData = data => {
    if (!vendorId) return;
    const historyAll = JSON.parse(localStorage?.getItem(historyKey)) || [];
    let newVendorHistory = { id: vendorId, list: data };
    const newHistoryAll = historyAll?.filter(item => item.id !== vendorId)?.slice(0, 19) || [];
    newHistoryAll?.push(newVendorHistory);
    localStorage?.setItem(historyKey, JSON.stringify(newHistoryAll));
  };

  const saveSearchedData = val => {
    if (!val) return;

    let data = historyData?.filter(item => item !== val)?.slice(0, 9) || [];
    data.unshift(val);
    setHistoryData(data);

    try {
      if (isVendor) {
        saveVendorSearchData(data);
        return;
      }
      localStorage.setItem(historyKey, JSON.stringify(data));
    } catch (error) {}
  };

  const deleHistoryItem = (e, val) => {
    e.stopPropagation();
    const data = historyData?.filter(item => item !== val);
    setHistoryData(data);
    try {
      if (isVendor) {
        saveVendorSearchData(data);
        return;
      }
      localStorage.setItem(historyKey, JSON.stringify(data));
    } catch (error) {}
  };

  const deleHistoryAll = () => {
    setHistoryData([]);
    try {
      if (isVendor) {
        const newHistoryAll =
          JSON.parse(localStorage?.getItem(historyKey))?.filter(item => item?.id !== vendorId) || [];
        localStorage.setItem(historyKey, JSON.stringify(newHistoryAll));
        return;
      }
      localStorage.removeItem(historyKey);
    } catch (error) {}
  };

  const checkSearchResult = (e, val, type?) => {
    searchInput?.current?.blur();
    val = val?.trim();
    e?.stopPropagation();
    setKeywordAndNotify(val);
    if (!val && !keyword) return;
    saveSearchedData(val);
    isTriggerType ? handleSearch(val, type) : handleSearch(val);
  };

  const fetchSuggestion = useDebounce(keyword => {
    getSearchProduct({ term: keyword }).then(({ object }) => {
      setSuggestList(object?.suggestions || []);
    });
  }, 300);

  const handleCancel = useCallback(e => {
    searchInput?.current?.blur();
    if (onCancel) {
      return onCancel(e => {
        defaultBack(router);
      });
    }

    if (isInGroupOrder) {
      router.back();
      return;
    }
    defaultBack(router);
  }, []);

  useImperativeHandle(ref, () => ({
    handleInputKeyword: handleInputKeyword,
    handleInputFocus: handleInputFocus,
    handleInputEnter: handleInputEnter,
    handleClearKeyword: handleClearKeyword,
    handleCancel: handleCancel
  }));

  useEffect(() => {
    if (isSupportHotKeyword || isSupportBought) {
      Promise.all([
        getBoughtBefore().then(({ object }) => object),
        getPopularSearch().then(({ object }) => object?.keywords || [])
      ]).then(([bought, popular]) => {
        setPageState({
          popular,
          bought
        });
      });
    }
  }, []);

  useEffect(() => {
    let data;
    try {
      data = localStorage.getItem(historyKey);
    } catch (error) {}
    let searchHistory = data ? JSON.parse(data) : [];
    if (isVendor) {
      searchHistory =
        searchHistory?.find(item => {
          return item?.id === vendorId;
        })?.list || [];
    }

    setHistoryData(searchHistory);

    if (!keyword) {
      setFocusAndNotify(true);
      searchInput?.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (props.visible) {
      new Tracker()
        .addPv(
          moduleName === 'global'
            ? PageNameV2.mweb_mkpl_global_search_landing
            : isVendor
            ? PageNameV2.mweb_mkpl_store_search_landing
            : PageNameV2.mweb_search_landing
        )
        .send();
      if (bodyFixed) {
        document.querySelector('body').classList.add('body-fixed');
      }
    }
    return () => {
      if (bodyFixed) {
        document.querySelector('body').classList.remove('body-fixed');
      }
    };
  }, [props.visible]);

  useEffect(() => {
    if (!keyword && props.visible) {
      setSuggestList([]);
      searchInput?.current?.focus();
      setFocusAndNotify(true);
    } else {
      isSupportAssoation && fetchSuggestion(keyword);
    }
  }, [keyword, props.visible]);

  const setIosFix = () => {
    if (props.visible) {
      setTimeout(() => {
        window.scrollTo({
          top: 0
        });
        if (document.getElementById('container')) {
          document.getElementById('container').scrollTop = 0;
        }
      }, 200);
    }
  };
  useEffect(() => {
    props.focusChange?.(focus);
    if (focus) {
      let timer = setTimeout(() => {
        document.documentElement.scrollTop = 0;
      }, 400);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [focus]);

  useEffect(() => {
    if (!!keyword) {
      setFocusAndNotify(false);
      searchInput?.current?.blur();
    }
  }, []);

  return (
    <div
      className={twMerge(
        'w-full md:w-[750px] mx-auto box-border bg-surface-100-bg',
        classNames(
          {
            'h-screen': focus && !streamlinedHeader && !embbedInsidePage,
            'h-full': focus && streamlinedHeader && !embbedInsidePage,
            'h-auto': focus && streamlinedHeader && embbedInsidePage === true
          },
          className
        )
      )}
      data-testid="wid-search-overlay"
      id="container">
      {!streamlinedHeader && (
        <div>
          {isInGroupOrder && isSayWeeeApp() && query?.fullscreen === '1' && fromDrawer && (
            <div className="h-[36px] bg-surface-100-fg-default w-full z-30"></div>
          )}
          {isInGroupOrder && (
            <div className="bg-surface-100-fg-default pb-2">
              <GroupOrderHeader
                vendorTitle={vendorTitle}
                shareInfo={shareInfo}
                showInvite={true}
                className="flex-shrink-0"
              />
            </div>
          )}
          <div
            className={twMerge(
              'flex items-center pr-500 pl-300 box-border bg-surface-100-bg',
              isInGroupOrder ? 'rounded-t-[10px] -mt-2 pt-5 pb-1' : 'h-[48px]',
              embbedInsidePage ? '!pr-0 !pl-0' : ''
            )}>
            {embbedInsidePage === true ? null : (
              <div
                data-testid="btn-back"
                className="w-auto h-[36px] flex items-center justify-center [&_.icon]:text-[32px] text-primary-1">
                <WeeeIcon type="iconBack" onClick={handleCancel} />
              </div>
            )}
            <div data-testid="wid-search-bar" className="relative h-10 flex-1 box-border flex items-center">
              <div
                className="absolute left-300"
                onClick={e => {
                  checkSearchResult(e, keyword, SearchTriggerType.active);
                }}>
                <WeeeIcon className={twMerge('text-primary-1 !text-xl')} type="iconcategory_select" />
              </div>
              <input
                data-testid="wid-search-input"
                className={classNames(
                  'box-border h-full w-full pl-900 enki-body-sm-medium rounded-full border-2 border-solid border-surface-100-hairline pr-300 outline-none focus:border-2 focus:border-btn-secondary-bg placeholder:text-input-100-fg-placeholder',
                  { 'pr-1000': !!keyword }
                )}
                type="text"
                autoFocus={inputAutoFocus}
                value={keyword}
                onChange={handleInputKeyword}
                maxLength={80}
                onFocus={handleInputFocus}
                onKeyDown={handleInputEnter}
                placeholder={placeholder || t('search_placeholder1')}
                aria-label={placeholder || t('search_placeholder1')}
                ref={searchInput}
              />
              {keyword && (
                <div
                  className="absolute right-100 mx-200 w-500 h-500 rounded-full bg-surface-100-fg-minor flex items-center justify-center"
                  onClick={handleClearKeyword}>
                  <WeeeIcon type="iconTimes" className="text-surface-100-bg !text-xl" />
                </div>
              )}
            </div>
            {isInGroupOrder ? <></> : showMiniCart ? <MiniCart className="ml-200 border-2 text-primary-1" /> : null}
          </div>
        </div>
      )}
      {focus &&
        !embbedInsidePage &&
        (!keyword ? (
          <div className="mx-auto mt-1 px-5 box-border" data-role="search-body">
            {hasHistory && historyData?.length > 0 && (
              <div className="pt-3 pb-1 overflow-hidden">
                <div className="flex justify-between items-center">
                  <div className="text-surface-100-fg-default enki-body-base-medium">{t('search_history')}</div>
                  <WeeeIcon
                    data-testid="wid-search-history-delete"
                    type="icondelete"
                    className="text-surface-100-fg-default !text-xl"
                    onClick={deleHistoryAll}
                  />
                </div>
                <div className="flex flex-wrap mt-1 overflow-hidden max-h-[120px] max-w-full">
                  {historyData?.map((item, index) => (
                    <div
                      className="max-w-full"
                      key={`history-${index}`}
                      onClick={e => {
                        checkSearchResult(e, item, SearchTriggerType.history);
                        commonClick({
                          mod_nm: SearchLandingMod.searchHistory,
                          mod_pos: 1,
                          co: {
                            target_nm: item,
                            target_pos: index,
                            target_type: ClickTargetType.searchTarget
                          },
                          page_key: 'mweb_search_landing'
                        });
                      }}>
                      <div
                        data-testid={`wid-search-history-item-${index}`}
                        className="h-7 mr-2 mt-2 pl-3 pr-1 rounded-full enki-body-2xs-medium flex items-center border-2 border-solid border-surface-100-hairline max-w-full overflow-hidden">
                        <span
                          className="text-surface-100-fg-minor line-clamp-1 flex-1 pr-1"
                          style={{ wordBreak: 'break-all' }}>
                          {item}
                        </span>
                        <WeeeIcon
                          data-testid="btn-delete-search-history-item"
                          type="iconTimes"
                          className="text-surface-100-fg-minor !text-[20px] !font-normal"
                          onClick={e => {
                            deleHistoryItem(e, item);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isSupportHotKeyword && popular?.length > 0 && (
              <div data-testid="mod-popular-search" className="pt-3 pb-1 overflow-hidden">
                <div className="flex justify-between items-center">
                  <div className="text-surface-100-fg-default enki-body-base-medium">{t('popular_search')}</div>
                </div>
                <div className="flex flex-wrap mt-1 overflow-hidden max-h-[120px]">
                  {popular.map((item, index) => (
                    <div
                      key={`hot-${index}`}
                      className="max-w-full"
                      data-testid={`btn-popular-search-${item}`}
                      onClick={e => {
                        checkSearchResult(e, item, SearchTriggerType.popular);
                        commonClick({
                          mod_nm: SearchLandingMod.popularSearch,
                          mod_pos: 2,
                          co: {
                            target_nm: item,
                            target_pos: index,
                            target_type: ClickTargetType.searchTarget
                          },
                          page_key: 'mweb_search_landing'
                        });
                      }}>
                      <div className="h-7 mr-200 mt-200 px-3 rounded-full enki-body-2xs-medium flex items-center border-2 border-solid border-surface-100-hairline">
                        {index < 3 && <WeeeIcon type="iconpopular" className="text-pricing-txt -ml-1 !text-[24px]" />}
                        <span className="text-surface-100-fg-minor max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isSupportBought && bought?.images?.length > 0 && (
              <div className="pt-3 pb-1 overflow-hidden">
                <div className="flex justify-between items-center">
                  <div className="text-surface-100-fg-default enki-body-base-medium">{t('bought_before')}</div>
                </div>
                <div className="mt-3 rounded-400 py-2 flex justify-between">
                  <ul className="flex w-[284px]">
                    {bought?.images?.map((url, index) => {
                      return (
                        <div
                          className="w-[62px] h-[62px] flex-[0_0_62px] flex items-center justify-center mr-3"
                          key={`${url}_${index}`}
                          onClick={() => {
                            commonClick({
                              mod_nm: SearchLandingMod.boughtBefore,
                              mod_pos: 3,
                              co: {
                                target_nm: bought?.url,
                                target_pos: index,
                                target_type: ClickTargetType.searchTarget
                              },
                              page_key: 'mweb_search_landing'
                            });
                          }}>
                          <Link prefetch={false} href={bought?.url}>
                            <a className="w-full h-full">
                              <CroppedImage
                                wrapperClassName="w-[62px] h-[62px]"
                                className="w-full h-full rounded-small"
                                url={url}
                                configs={{ renderWidth: 62 }}
                                alt={`weee search overlay bought ${index}`}
                                type="img"
                              />
                            </a>
                          </Link>
                        </div>
                      );
                    })}
                  </ul>
                  <a className="text-link-base-1 enki-heading-2xl flex items-center justify-end" href={bought?.url}>
                    <WeeeIcon type="iconArrowRight" className="!text-[20px] !font-normal" />
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            className={twMerge(
              'mx-5 rounded-400 overflow-y-scroll',
              streamlinedHeader ? 'h-[calc(100dvh-115px)]' : 'h-[calc(100vh-102px)]',
              classNames({ 'mt-10': !isSupportAssoation })
            )}>
            {suggestList.map((item, index) => {
              return (
                <div key={`${item.label}-${index}`} className="py-2" style={{ borderBottom: '1px solid #f3f3f3' }}>
                  <div
                    className="flex items-center px-2.5 min-h-[36px]"
                    onClick={e => {
                      setFocusAndNotify(false);
                      checkSearchResult(e, item.value, SearchTriggerType.suggestion);
                      commonClick({
                        mod_nm: SearchLandingMod.searchBar,
                        mod_pos: 0,
                        co: {
                          target_nm: item.value,
                          target_type: ClickTargetType.searchTarget
                        },
                        page_key: 'mweb_search_landing'
                      });
                    }}>
                    {!!item?.img_url && (
                      <img
                        alt="Search item image"
                        className="w-[36px] h-[36px] rounded-200 mr-3"
                        src={item?.img_url}></img>
                    )}
                    <div
                      className={'flex-1 text-surface-100-fg-default enki-body-2xs [&_span]:font-normal'}
                      style={{
                        fontWeight: 600
                      }}
                      dangerouslySetInnerHTML={{
                        __html: replaceHighlight(item.label, keyword)
                      }}
                    />
                    <WeeeIcon className="!text-[20px] !font-normal" type="iconArrowRight" />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
});

export const SearchOverlay = forwardRef((props: SearchOverlayProps, ref) => {
  const { children, popupProps } = props;
  const { visible, hide, show } = useLazy();

  const handleCancel = () => {
    hide();
  };

  useImperativeHandle(ref, () => ({
    show: show,
    hide: hide
  }));

  return (
    <>
      {!!children &&
        cloneElement(children as any, {
          onClick: show
        })}
      <Popup animationType="none" position="left" show={visible} destroyOnClose {...popupProps}>
        <SearchContainer visible={visible} {...props} onCancel={handleCancel} />
      </Popup>
    </>
  );
});

export default SearchContainer;
